import { createStore } from '@stencil/store';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, updateDoc } from 'firebase/firestore';

import { IUser } from '../interfaces/User';
import { auth, db } from '../firebase';
import { ICompany } from '../interfaces/Company';
import { IForm } from '../interfaces/Form';

const { state, onChange } = createStore<{
  initialised: boolean;
  user: IUser | null;
  companies: ICompany[] | null;
  forms: IForm[] | null;
  isAuthenticated: boolean;
}>({
  initialised: false,
  user: null,
  companies: null,
  forms: null,
  isAuthenticated: false,
});

onAuthStateChanged(auth, async (user) => {
  if (user) {
    const userData: IUser = {
      uid: user.uid,
      email: user.email || '',
    };

    const userDocRef = doc(db, `users/${user.uid}`);
    const userDocSnapshot = await getDoc(userDocRef);

    if (userDocSnapshot.exists()) {
      const userDocData = userDocSnapshot.data();
      userData.displayName = userDocData?.displayName || user.email || 'User';
      userData.selectedCompany = userDocData?.selectedCompany || null;
      userData.companies = userDocData?.companies || {};
    } else {
      userData.displayName = user.email || 'User';
    }

    state.user = userData;
    state.isAuthenticated = true;
  
    await getCompanies();
    await getForms();

    state.initialised = true;
  } else {
    state.user = null;
    state.isAuthenticated = false;
    state.initialised = true;

    console.log('No user logged in');
  }
});

const getForms = async () => {
  if (state.user && state.isAuthenticated && state.user.selectedCompany) {
    const formsCollectionRef = collection(db, `companies/${state.user.selectedCompany}/forms`);
    const formsCollectionSnapshot = await getDocs(formsCollectionRef);
    
    state.forms = formsCollectionSnapshot.docs.map((doc) => {
      return { ...doc.data(), id: doc.id } as IForm;
    });
  } else {
    state.forms = null;
  }
}

const getCompanies = async () => {
  if (state.user && state.isAuthenticated) {
    const userDocRef = doc(db, `users/${state.user.uid}`);
    const userDocSnapshot = await getDoc(userDocRef);

    if (userDocSnapshot.exists()) {
      const userData = userDocSnapshot.data();
      if (userData && userData.companies) {
        const companies = userData.companies;
        const companyIds = Object.keys(companies);

        await Promise.all(companyIds.map(async (companyId) => {
          const companyDocRef = doc(db, `companies/${companyId}`);
          const companyDocSnapshot = await getDoc(companyDocRef);
          if (companyDocSnapshot.exists()) {
            const companyData = companyDocSnapshot.data();

            if (state.companies) {
              state.companies.push({
                id: companyId,
                name: companyData?.name || 'Unknown Company',
                settings: companyData?.settings || {},
              });
            } else {
              state.companies = [{
                id: companyId,
                name: companyData?.name || 'Unknown Company',
                settings: companyData?.settings || {},
              }];
            }
          }
        }));

        if (!userData.selectedCompany) {
          state.user.selectedCompany = state.companies?.sort((a, b) => a.name.localeCompare(b.name))[0].id || null;
        }
      }
    }
  }
}

const updateCurrentUser = async (user: IUser) => {
  if (user && user.uid && state.initialised) {
    const userDocRef = doc(db, `users/${user.uid}`);

    await getForms();

    await updateDoc(userDocRef, {
      displayName: user.displayName,
      selectedCompany: user.selectedCompany,
    });
  } else {
    console.error('Invalid user data:', user);
  }
}

onChange('user', async (newValue) => {
  state.isAuthenticated = !!newValue;
  if (newValue && state.initialised) {
    await updateCurrentUser(newValue);
  }
});

export default {
  state,
  onChange,
  setUser(user: IUser) {
    state.user = user;
    state.isAuthenticated = !!user;
  },
  clearUser() {
    state.user = null;
    state.isAuthenticated = false;
  },
};